import { useEffect, useRef } from 'react';

/**
 * Fond animé du bandeau « Smart Building » et de la connexion : un réseau électrique stylisé
 * (compteur → tableau → zones → équipements) parcouru par des impulsions d'énergie à traînée,
 * étincelles à l'arrivée sur chaque nœud, balayage depuis le compteur, grille au sol en perspective.
 * Canvas 2D, en pause quand l'onglet est caché, statique si l'utilisateur préfère moins d'animations.
 */

type Node = { x: number; y: number; r: number; depth: number; phase: number };
type Edge = { a: number; b: number };
type Pulse = { e: number; t: number; v: number; hue: number };
type Spark = { x: number; y: number; vx: number; vy: number; life: number; hue: number };

const CYAN = 190, BLUE = 210, GREEN = 160;

function buildNetwork(w: number, h: number, seed: number) {
  let s = seed >>> 0;
  const rnd = () => { s = (s * 1664525 + 1013904223) >>> 0; return s / 4294967296; };
  const nodes: Node[] = [];
  const edges: Edge[] = [];
  // Arbre électrique : une racine (compteur) à gauche, 3 niveaux vers la droite.
  const levels = [1, 3, 7, 14];
  const cols = levels.length;
  let prevStart = 0;
  for (let c = 0; c < cols; c++) {
    const count = levels[c];
    const x0 = w * (0.08 + (c / (cols - 1)) * 0.84);
    const start = nodes.length;
    for (let i = 0; i < count; i++) {
      const y = h * ((i + 0.5) / count) + (rnd() - 0.5) * (h / count) * 0.5;
      const x = x0 + (rnd() - 0.5) * w * 0.06;
      nodes.push({ x, y, r: c === 0 ? 5 : c === cols - 1 ? 1.8 : 3 - c * 0.4, depth: c, phase: rnd() * Math.PI * 2 });
      if (c > 0) {
        // parent : le nœud du niveau précédent le plus proche verticalement
        let best = prevStart, bd = Infinity;
        for (let p = prevStart; p < start; p++) { const d = Math.abs(nodes[p].y - y); if (d < bd) { bd = d; best = p; } }
        edges.push({ a: best, b: start + i });
      }
    }
    prevStart = start;
  }
  return { nodes, edges };
}

export default function EnergyFlowBackground({ className = '' }: { className?: string }) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current; if (!canvas) return;
    const ctx = canvas.getContext('2d'); if (!ctx) return;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    let raf = 0, w = 0, h = 0, running = true, last = performance.now(), t = 0;
    let net = buildNetwork(1, 1, 7);
    const pulses: Pulse[] = [];
    const sparks: Spark[] = [];

    const newPulse = (): Pulse => ({ e: Math.floor(Math.random() * net.edges.length), t: Math.random(), v: 0.25 + Math.random() * 0.45, hue: Math.random() < 0.15 ? GREEN : Math.random() < 0.5 ? CYAN : BLUE });
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = Math.max(1, Math.round(rect.width)); h = Math.max(1, Math.round(rect.height));
      canvas.width = Math.round(w * dpr); canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      net = buildNetwork(w, h, 7);
      pulses.length = 0;
      for (let i = 0; i < Math.min(28, net.edges.length); i++) pulses.push(newPulse());
    };
    // point sur la courbe de Bézier cubique (mêmes points de contrôle que le tracé des liaisons)
    const bez = (a: Node, b: Node, u: number) => { const mx = (a.x + b.x) / 2, iu = 1 - u; return [iu * iu * iu * a.x + 3 * iu * iu * u * mx + 3 * iu * u * u * mx + u * u * u * b.x, iu * iu * iu * a.y + 3 * iu * iu * u * a.y + 3 * iu * u * u * b.y + u * u * u * b.y]; };

    const drawGrid = () => {
      // grille en perspective au sol, qui défile lentement vers le spectateur
      const horizon = h * 0.42; ctx.save(); ctx.strokeStyle = 'rgba(140, 200, 255, 0.07)'; ctx.lineWidth = 1;
      const rows = 12; const scroll = (t * 0.08) % 1;
      for (let i = 0; i < rows; i++) {
        const z = (i + scroll) / rows; const y = horizon + Math.pow(z, 2.2) * (h - horizon) * 1.2;
        if (y > h) continue; ctx.globalAlpha = 0.35 + z * 0.65; ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke();
      }
      ctx.globalAlpha = 1;
      for (let i = -8; i <= 8; i++) { ctx.beginPath(); ctx.moveTo(w / 2 + i * w * 0.04, horizon); ctx.lineTo(w / 2 + i * w * 0.32, h * 1.3); ctx.stroke(); }
      ctx.restore();
    };

    const frame = (now: number) => {
      if (!running) return;
      const dt = Math.max(0, Math.min(0.05, (now - last) / 1000)); last = now; t += dt;
      ctx.clearRect(0, 0, w, h);
      drawGrid();
      const { nodes, edges } = net;
      // liaisons
      ctx.lineWidth = 1;
      for (const e of edges) {
        const a = nodes[e.a], b = nodes[e.b];
        const g = ctx.createLinearGradient(a.x, a.y, b.x, b.y);
        g.addColorStop(0, 'rgba(103, 232, 249, 0.22)'); g.addColorStop(1, 'rgba(96, 165, 250, 0.08)');
        ctx.strokeStyle = g; ctx.beginPath(); ctx.moveTo(a.x, a.y);
        const mx = (a.x + b.x) / 2; ctx.bezierCurveTo(mx, a.y, mx, b.y, b.x, b.y); ctx.stroke();
      }
      // impulsions d'énergie le long des liaisons
      ctx.globalCompositeOperation = 'lighter';
      for (let i = 0; i < pulses.length; i++) {
        const p = pulses[i]; p.t += dt * p.v;
        if (p.t >= 1) {
          // arrivée : étincelle sur le nœud, puis on repart sur une autre liaison
          const n = nodes[edges[p.e].b];
          for (let k = 0; k < 4; k++) { const ang = Math.random() * Math.PI * 2, sp = 10 + Math.random() * 30; sparks.push({ x: n.x, y: n.y, vx: Math.cos(ang) * sp, vy: Math.sin(ang) * sp, life: 1, hue: p.hue }); }
          pulses[i] = newPulse(); pulses[i].t = 0; continue;
        }
        const e = edges[p.e], a = nodes[e.a], b = nodes[e.b]; const [x, y] = bez(a, b, p.t);
        const rg = ctx.createRadialGradient(x, y, 0, x, y, 9);
        rg.addColorStop(0, `hsla(${p.hue}, 100%, 85%, 0.95)`); rg.addColorStop(0.35, `hsla(${p.hue}, 100%, 65%, 0.5)`); rg.addColorStop(1, `hsla(${p.hue}, 100%, 60%, 0)`);
        ctx.fillStyle = rg; ctx.beginPath(); ctx.arc(x, y, 9, 0, Math.PI * 2); ctx.fill();
        // traînée
        const [x0, y0] = bez(a, b, Math.max(0, p.t - 0.08));
        ctx.strokeStyle = `hsla(${p.hue}, 100%, 75%, 0.35)`; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.moveTo(x0, y0); ctx.lineTo(x, y); ctx.stroke();
      }
      // étincelles
      for (let i = sparks.length - 1; i >= 0; i--) {
        const s = sparks[i]; s.life -= dt * 2.2; if (s.life <= 0) { sparks.splice(i, 1); continue; }
        s.x += s.vx * dt; s.y += s.vy * dt; s.vx *= 0.92; s.vy *= 0.92;
        ctx.fillStyle = `hsla(${s.hue}, 100%, 80%, ${s.life * 0.8})`; ctx.beginPath(); ctx.arc(s.x, s.y, Math.max(0.1, 1.2 + s.life), 0, Math.PI * 2); ctx.fill();
      }
      // nœuds : halo respirant, le compteur (racine) plus marqué
      for (const n of nodes) {
        const breath = 0.6 + 0.4 * Math.sin(t * 1.6 + n.phase);
        const R = Math.max(0.5, n.r * (2.2 + breath));
        const rg = ctx.createRadialGradient(n.x, n.y, 0, n.x, n.y, R * 2.4);
        rg.addColorStop(0, `rgba(165, 243, 252, ${0.55 * breath})`); rg.addColorStop(1, 'rgba(103, 232, 249, 0)');
        ctx.fillStyle = rg; ctx.beginPath(); ctx.arc(n.x, n.y, R * 2.4, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = n.depth === 0 ? '#e0fbff' : 'rgba(207, 250, 254, 0.9)'; ctx.beginPath(); ctx.arc(n.x, n.y, n.r, 0, Math.PI * 2); ctx.fill();
      }
      // balayage radar depuis le compteur
      const root = nodes[0]; const sweep = (t * 0.35) % 1; const sr = Math.max(0, sweep * w * 1.1);
      ctx.strokeStyle = `rgba(103, 232, 249, ${0.25 * (1 - sweep)})`; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.arc(root.x, root.y, sr, -Math.PI / 2, Math.PI / 2); ctx.stroke();
      ctx.globalCompositeOperation = 'source-over';
      raf = requestAnimationFrame(frame);
    };

    const ro = new ResizeObserver(() => { resize(); if (reduce) { frame(performance.now()); running = false; } });
    ro.observe(canvas);
    resize();
    const onVis = () => { if (document.hidden) { running = false; cancelAnimationFrame(raf); } else if (!reduce) { running = true; last = performance.now(); raf = requestAnimationFrame(frame); } };
    document.addEventListener('visibilitychange', onVis);
    if (reduce) { frame(performance.now()); running = false; } else raf = requestAnimationFrame(frame);
    return () => { running = false; cancelAnimationFrame(raf); ro.disconnect(); document.removeEventListener('visibilitychange', onVis); };
  }, []);

  return <canvas ref={ref} aria-hidden="true" className={`absolute inset-0 w-full h-full pointer-events-none ${className}`} />;
}
